function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
const API_URL = '/api';
export class SSOConfigApiError extends Error {
    constructor(message, status){
        super(message), _define_property(this, "status", void 0), this.status = status;
        this.name = 'SSOConfigApiError';
    }
}
export async function fetchSSOConfig() {
    try {
        const response = await fetch(`${API_URL}/sso/config`, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (!response.ok) {
            if (response.status === 404) {
                // Return default config when none exists
                return getDefaultConfig();
            }
            throw new SSOConfigApiError(`Failed to fetch SSO config: ${response.status}`, response.status);
        }
        const data = await response.json();
        return normalizeConfig(data);
    } catch (error) {
        if (error instanceof SSOConfigApiError) {
            throw error;
        }
        throw new SSOConfigApiError(error instanceof Error ? error.message : 'Failed to fetch SSO configuration');
    }
}
export async function saveSSOConfig(payload) {
    try {
        const response = await fetch(`${API_URL}/sso/config`, {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new SSOConfigApiError(errorData.error || `Failed to save SSO config: ${response.status}`, response.status);
        }
        const data = await response.json();
        return normalizeConfig(data);
    } catch (error) {
        if (error instanceof SSOConfigApiError) {
            throw error;
        }
        throw new SSOConfigApiError(error instanceof Error ? error.message : 'Failed to save SSO configuration');
    }
}
export function getDefaultConfig() {
    return {
        email_password_enabled: true,
        google_enabled: false,
        google_client_id: '',
        microsoft_enabled: false,
        microsoft_client_id: '',
        microsoft_tenant_id: '',
        google_saml_enabled: false,
        google_saml_metadata_url: '',
        google_saml_entity_id: '',
        google_saml_sso_url: '',
        google_saml_certificate: '',
        google_saml_email_attribute: 'email',
        microsoft_saml_enabled: false,
        microsoft_saml_metadata_url: '',
        microsoft_saml_entity_id: '',
        microsoft_saml_sso_url: '',
        microsoft_saml_certificate: '',
        microsoft_saml_email_attribute: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
    };
}
function normalizeConfig(data) {
    const defaults = getDefaultConfig();
    // Backend returns flat format, just normalize with defaults
    return {
        email_password_enabled: data.email_password_enabled ?? defaults.email_password_enabled,
        google_enabled: data.google_enabled ?? defaults.google_enabled,
        google_client_id: data.google_client_id ?? defaults.google_client_id,
        microsoft_enabled: data.microsoft_enabled ?? defaults.microsoft_enabled,
        microsoft_client_id: data.microsoft_client_id ?? defaults.microsoft_client_id,
        microsoft_tenant_id: data.microsoft_tenant_id ?? defaults.microsoft_tenant_id,
        google_saml_enabled: data.google_saml_enabled ?? defaults.google_saml_enabled,
        google_saml_metadata_url: data.google_saml_metadata_url ?? defaults.google_saml_metadata_url,
        google_saml_entity_id: data.google_saml_entity_id ?? defaults.google_saml_entity_id,
        google_saml_sso_url: data.google_saml_sso_url ?? defaults.google_saml_sso_url,
        google_saml_certificate: data.google_saml_certificate ?? defaults.google_saml_certificate,
        google_saml_email_attribute: data.google_saml_email_attribute ?? defaults.google_saml_email_attribute,
        microsoft_saml_enabled: data.microsoft_saml_enabled ?? defaults.microsoft_saml_enabled,
        microsoft_saml_metadata_url: data.microsoft_saml_metadata_url ?? defaults.microsoft_saml_metadata_url,
        microsoft_saml_entity_id: data.microsoft_saml_entity_id ?? defaults.microsoft_saml_entity_id,
        microsoft_saml_sso_url: data.microsoft_saml_sso_url ?? defaults.microsoft_saml_sso_url,
        microsoft_saml_certificate: data.microsoft_saml_certificate ?? defaults.microsoft_saml_certificate,
        microsoft_saml_email_attribute: data.microsoft_saml_email_attribute ?? defaults.microsoft_saml_email_attribute
    };
}
export function validateConfig(config, secrets) {
    if (config.google_enabled && (!config.google_client_id || !secrets.google_client_secret)) {
        return 'Google Client ID and Secret are required when Google OAuth is enabled';
    }
    if (config.microsoft_enabled && (!config.microsoft_client_id || !secrets.microsoft_client_secret || !config.microsoft_tenant_id)) {
        return 'Microsoft Client ID, Secret, and Tenant ID are required when Microsoft OAuth is enabled';
    }
    return null;
}
export async function parseSAMLMetadata(metadataXML, provider) {
    try {
        const response = await fetch(`${API_URL}/sso/parse-metadata`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                metadata_xml: metadataXML,
                provider
            })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new SSOConfigApiError(errorData.error || `Failed to parse SAML metadata: ${response.status}`, response.status);
        }
        return await response.json();
    } catch (error) {
        if (error instanceof SSOConfigApiError) {
            throw error;
        }
        throw new SSOConfigApiError(error instanceof Error ? error.message : 'Failed to parse SAML metadata');
    }
}
